/**
 * LocationMap — the address-tab map preview.
 *
 * Added 2026-08-07 (Nick). Two things worth knowing before editing this file:
 *
 * 1. **There was no mapping capability in the kit.** No map library is bundled
 *    anywhere in `cd-kit-composed`, and the declared `geo` contract family
 *    (`suggestAddresses` / `validateAddress` / `resolveAddress`) was wired to
 *    nothing. Per `skills/Maps & geography`, a street-level interactive map is
 *    Leaflet + OpenStreetMap tiles — loaded through the EXACT pinned,
 *    hash-verified tags below. Do not change the version, URL or integrity
 *    hash of either tag, and do not add other CDN scripts beside them: the
 *    tags fail closed if tampered with, and anything else would load
 *    unverified. The `© OpenStreetMap contributors` attribution is a licence
 *    requirement, not decoration — never remove it.
 *
 * 2. **Coordinates are DATA, not something this component derives.** A map that
 *    guesses where an address is would be confidently wrong. `latitude` /
 *    `longitude` are real optional fields on the `Location` contract; when they
 *    are absent this renders an honest "not located yet" state rather than
 *    dropping a pin at 0,0 (which is a real place in the Gulf of Guinea).
 *    Resolving an address to coordinates is the geo family's job.
 *
 * Loading is lazy and idempotent: the CSS/JS pair is injected once per document
 * and every later mount reuses it, so opening ten location records does not
 * fetch Leaflet ten times.
 */
import { useEffect, useRef, useState } from "react";
import { Icon, T } from "@v0y4ge/jrni-ui";
import { useT } from "../../lib/i18n";

const LEAFLET_CSS = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
const LEAFLET_CSS_SRI = "sha384-sHL9NAb7lN7rfvG5lfHpm643Xkcjzp4jFvuavGOndn6pjVqS6ny56CAt3nsEVT4H";
const LEAFLET_JS = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
const LEAFLET_JS_SRI = "sha384-cxOPjt7s7Iz04uaHJceBmS+qpjv2JkIHNVcuOrM+YHwZOmJGBXI00mdUXEq65HTH";

/** Minimal shape of the bits of Leaflet this component touches. */
interface LeafletMap {
  setView: (c: [number, number], z: number) => LeafletMap;
  remove: () => void;
  invalidateSize: () => void;
}
interface LeafletMarker { setLatLng: (c: [number, number]) => LeafletMarker }
interface LeafletNS {
  map: (el: HTMLElement, opts?: Record<string, unknown>) => LeafletMap;
  tileLayer: (url: string, opts: Record<string, unknown>) => { addTo: (m: LeafletMap) => unknown };
  marker: (c: [number, number], opts?: Record<string, unknown>) => LeafletMarker & { addTo: (m: LeafletMap) => LeafletMarker };
}

let loader: Promise<LeafletNS> | null = null;

/** Inject the pinned CSS + JS once; resolve with the global `L`. */
function loadLeaflet(): Promise<LeafletNS> {
  if (loader) return loader;
  loader = new Promise<LeafletNS>((resolve, reject) => {
    const w = window as unknown as { L?: LeafletNS };
    if (w.L) { resolve(w.L); return; }
    if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = LEAFLET_CSS;
      link.integrity = LEAFLET_CSS_SRI;
      link.crossOrigin = "anonymous";
      document.head.appendChild(link);
    }
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${LEAFLET_JS}"]`);
    const onDone = () => (w.L ? resolve(w.L) : reject(new Error("Leaflet loaded but window.L is missing")));
    if (existing) { existing.addEventListener("load", onDone); existing.addEventListener("error", () => reject(new Error("Leaflet failed to load"))); return; }
    const s = document.createElement("script");
    s.src = LEAFLET_JS;
    s.integrity = LEAFLET_JS_SRI;
    s.crossOrigin = "anonymous";
    s.async = true;
    s.onload = onDone;
    s.onerror = () => reject(new Error("Leaflet failed to load"));
    document.head.appendChild(s);
  });
  return loader;
}

export interface LocationMapProps {
  latitude?: number | null;
  longitude?: number | null;
  /** Marker tooltip — the location's name. */
  label?: string;
  /** One-line address, shown under the map so the pin can be sanity-checked. */
  caption?: string;
  height?: number;
  /** Rendered in the empty state — e.g. a "Locate from address" button. */
  action?: React.ReactNode;
}

export default function LocationMap({ latitude, longitude, label, caption, height = 220, action }: LocationMapProps) {
  const t = useT();
  const hostRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<LeafletMap | null>(null);
  const markerRef = useRef<LeafletMarker | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  const hasCoords = typeof latitude === "number" && typeof longitude === "number"
    && Number.isFinite(latitude) && Number.isFinite(longitude);

  useEffect(() => {
    if (!hasCoords) return;
    let cancelled = false;
    const centre: [number, number] = [latitude as number, longitude as number];

    loadLeaflet().then((L) => {
      if (cancelled || !hostRef.current) return;
      if (!mapRef.current) {
        const map = L.map(hostRef.current, { scrollWheelZoom: false, attributionControl: true });
        /* The OpenStreetMap public tile server — a genuinely tenant-agnostic
         * third-party host, identical for every tenant, and NOT something the
         * tenant public-host facility could or should resolve. This is the
         * `ui-config` case the hardcoded-host guard documents, not a leak. // ui-config */
        L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
          attribution: "© OpenStreetMap contributors",
          maxZoom: 19,
        }).addTo(map);
        map.setView(centre, 15);
        markerRef.current = L.marker(centre, { title: label ?? "" }).addTo(map);
        mapRef.current = map;
        /* The panel can mount inside a tab that was display:none a frame ago;
           Leaflet sizes tiles from clientHeight, so re-measure once laid out. */
        setTimeout(() => { try { mapRef.current?.invalidateSize(); } catch { /* torn down */ } }, 60);
      } else {
        mapRef.current.setView(centre, 15);
        markerRef.current?.setLatLng(centre);
        mapRef.current.invalidateSize();
      }
      setReady(true);
    }).catch((e: unknown) => {
      if (!cancelled) setError(e instanceof Error ? e.message : String(e));
    });

    return () => { cancelled = true; };
  }, [hasCoords, latitude, longitude, label]);

  /* Tear the map down only when the component itself goes away. */
  useEffect(() => () => {
    try { mapRef.current?.remove(); } catch { /* already gone */ }
    mapRef.current = null;
    markerRef.current = null;
  }, []);

  if (!hasCoords) {
    return (
      <div style={{ border: `1px dashed ${T.n4}`, borderRadius: 10, background: T.canvas, padding: "22px 18px", display: "flex", flexDirection: "column", alignItems: "center", gap: 8, textAlign: "center" }}>
        <span style={{ width: 38, height: 38, borderRadius: 10, background: T.white, color: T.soft, display: "inline-flex", alignItems: "center", justifyContent: "center", border: `1px solid ${T.border}` }}>
          <Icon.loc size={17} />
        </span>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: T.n1 }}>{t("locations.map.notLocatedYet", "Not located yet")}</div>
        <div style={{ fontSize: 12.5, color: T.soft, maxWidth: 380, lineHeight: 1.45 }}>
          {t("locations.map.thisAddressHasNoCoordinates", "This address has no coordinates on record, so there is nothing to pin. Resolve it from the address to place it on the map.")}
        </div>
        {action}
      </div>
    );
  }

  return (
    <div>
      <div style={{ position: "relative", borderRadius: 10, overflow: "hidden", border: `1px solid ${T.border}`, background: T.n6 }}>
        <div ref={hostRef} style={{ height, width: "100%" }} />
        {!ready && !error ? (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12.5, color: T.soft, background: T.n6 }}>
            {t("locations.map.loadingMap", "Loading map…")}
          </div>
        ) : null}
        {error ? (
          <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", gap: 4, alignItems: "center", justifyContent: "center", background: T.n6, padding: 16, textAlign: "center" }}>
            <span style={{ color: T.orange1 }}><Icon.warn size={16} /></span>
            <div style={{ fontSize: 12.5, color: T.n2 }}>{t("locations.map.theMapCouldnTLoad", "The map couldn't load.")}</div>
            <div style={{ fontSize: 11.5, color: T.soft, fontFamily: T.fontMono }}>{error}</div>
          </div>
        ) : null}
      </div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 6 }}>
        {caption ? <span style={{ fontSize: 12, color: T.soft, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{caption}</span> : null}
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 11, fontFamily: T.fontMono, color: T.n3, whiteSpace: "nowrap" }}>
          {(latitude as number).toFixed(5)}, {(longitude as number).toFixed(5)}
        </span>
      </div>
    </div>
  );
}
